'use memo';

import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { HStack, VStack } from '@/components/Layout';
import { FullScreenOverlay } from '@/components/Loading';
import { AddStepperCompleteDialog } from '@/components/Stepper';
import { toast } from '@/components/ui/use-toast';
import { DEVICE_ASSIGNMENT_RULE_WIZARD_HASH } from '@/features/device-assignment-rule/constants/wizard';
import { useCreateDeviceAssignmentRuleMutation, useDeviceAssignmentRuleGroupOptions } from '@/features/device-assignment-rule/query';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { featureFlags } from '@/lib/env';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { DeviceAssignmentRuleShell } from '../-components/DeviceAssignmentRuleShell';
import { createEmptyRuleFormDraft, draftToFormInput, isOrderInGroupValid, parseDeviceAssignmentRuleForm, type RuleFormDraft } from '../-components/rule-form-schema';
import { RuleAssignmentPreviewSection } from '../-components/RuleAssignmentPreviewSection';
import { RuleBasicFields, RuleTargetFields } from '../-components/RuleFormFields';
import { DEVICE_ASSIGNMENT_FEATURE_FLAG } from '../../-constants/featureFlags';
import AddStepperNavigation from './-components/AddStepperNavigation';
import AddStepperPanelProgress from './-components/AddStepperPanelProgress';
import AddStepperStepTitleCard from './-components/AddStepperStepTitleCard';
import RuleConfirmSummary from './-components/RuleConfirmSummary';
import useAssignmentRuleWizardHash from './-hooks/useAssignmentRuleWizardHash';
export const Route = createFileRoute('/_staff/devices/_list/rule-and-policy/new/')({
  // 本番非公開: FeatureFlag OFF の間は直接 URL でも遷移させず ComingSoonCover へ戻す
  beforeLoad: () => {
    if (!featureFlags.check(DEVICE_ASSIGNMENT_FEATURE_FLAG)) {
      throw redirect({
        to: '/devices/rule-and-policy'
      });
    }
  },
  component: RouteComponent
});
const BASE_LINK = '/devices/rule-and-policy/new';
function RouteComponent() {
  const {
    hash
  } = useAssignmentRuleWizardHash();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.ruleAndPolicy'
  });
  const [draft, setDraft] = useState<RuleFormDraft>(createEmptyRuleFormDraft);
  const [createdRuleId, setCreatedRuleId] = useState<string | null>(null);
  useDocumentTitle(t('createRule'));
  const {
    data: ruleGroups = [],
    isError: isRuleGroupsError
  } = useDeviceAssignmentRuleGroupOptions();
  const createMutation = useCreateDeviceAssignmentRuleMutation({
    onSuccess: id => setCreatedRuleId(id)
  });
  const selectedRuleGroup = ruleGroups.find(group => group.id === draft.ruleGroupId);
  const patchDraft = (patch: Partial<RuleFormDraft>) => {
    setDraft(prev => ({
      ...prev,
      ...patch
    }));
  };
  const orderInGroupError = isOrderInGroupValid(draft) ? undefined : t('form.validation.orderInGroupInvalid');
  const handleCreate = () => {
    const result = parseDeviceAssignmentRuleForm(draftToFormInput(draft, selectedRuleGroup?.memberCount));
    if (!result.success) {
      toast({
        description: t('wizard.submitError'),
        variant: 'destructive'
      });
      return;
    }
    createMutation.mutate(result.data);
  };
  const isComplete = createdRuleId != null;
  return <DeviceAssignmentRuleShell>
      <HStack className="min-h-0 w-full flex-1 gap-9 p-10">
        <AddStepperNavigation baseLink={BASE_LINK} currentHash={hash} />

        <VStack className="h-full min-w-0 w-full gap-6">
          {createMutation.isPending ? <FullScreenOverlay /> : null}

          {!createMutation.isPending ? <>
              <AddStepperStepTitleCard hash={hash} />
              <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
                {match(hash).with(DEVICE_ASSIGNMENT_RULE_WIZARD_HASH.BASIC, () => <RuleBasicFields draft={draft} onChange={patchDraft} ruleGroups={ruleGroups} ruleGroupsLoadError={isRuleGroupsError} fieldErrors={orderInGroupError ? {
              orderInGroup: orderInGroupError
            } : undefined} />).with(DEVICE_ASSIGNMENT_RULE_WIZARD_HASH.TARGET, () => <RuleTargetFields draft={draft} onChange={patchDraft} />).with(DEVICE_ASSIGNMENT_RULE_WIZARD_HASH.CONFIRM, () => <>
                      <RuleConfirmSummary draft={draft} ruleGroupName={selectedRuleGroup?.name ?? null} ruleGroupMemberCount={selectedRuleGroup?.memberCount} />
                      <RuleAssignmentPreviewSection draft={{
                peopleGroupIds: draft.peopleGroupIds,
                deviceGroupId: draft.deviceGroupId
              }} deviceGroupName={draft.deviceGroupLabel || null} />
                    </>).exhaustive()}
              </div>

              <AddStepperPanelProgress baseLink={BASE_LINK} currentHash={hash} draft={draft} onSubmit={handleCreate} />
            </> : null}

          {isComplete ? <AddStepperCompleteDialog completeMessage={t('wizard.completeDialog.createMessage')} completeLinkButtonText={t('wizard.completeDialog.detailLink')} completeLinkUrl={`/devices/rule-and-policy/${createdRuleId}`} /> : null}
        </VStack>
      </HStack>
    </DeviceAssignmentRuleShell>;
}